import { uiSurface } from "shared/ui/uiSurface"
import { Box, Stack, TextField, Typography } from "@mui/material"
import { Dispatch, FC } from "react"
import { useTranslation } from "react-i18next"
import { ClientSummary } from "../../../apis/appApi"
import { AddressList } from "./AddressList"
import { CustomerNameAutocomplete } from "./CustomerNameAutocomplete"
import { PhoneNumberList } from "./PhoneNumberList"
import { TagSelector } from "./TagSelector"
import type { AddressEntry, OrderCreateFormState, PhoneEntry } from "./useOrderCreateForm"

type Tag = { id: number; label: string; color: string; icon?: string; shortLabel?: string; displayOption?: string }

type Props = {
  state: OrderCreateFormState
  dispatch: Dispatch<any>
  clientTags: Tag[]
}

export const CustomerSection: FC<Props> = ({ state, dispatch, clientTags }) => {
  const { t } = useTranslation()

  return (
    <Box {...uiSurface("UI-DLV2-035")}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
        {t("orderCreate.customer")}
      </Typography>
      <Stack spacing={2}>
        <CustomerNameAutocomplete
          lastName={state.lastName}
          firstName={state.firstName}
          selectedClient={state.selectedClient}
          onLastNameChange={(v) => dispatch({ type: "SET_LAST_NAME", value: v })}
          onFirstNameChange={(v) => dispatch({ type: "SET_FIRST_NAME", value: v })}
          onSelectClient={(client: ClientSummary) =>
            dispatch({ type: "SELECT_CLIENT", client: { id: client.id, firstName: client.firstName, lastName: client.lastName, organisation: client.organisation } })
          }
          onClearClient={() => dispatch({ type: "CLEAR_CLIENT" })}
        />

        <PhoneNumberList
          phoneNumbers={state.phoneNumbers}
          onSetPhone={(idx, field, val) =>
            dispatch({ type: "SET_PHONE", index: idx, field: field as keyof PhoneEntry, value: val })
          }
          onAddPhone={() => dispatch({ type: "ADD_PHONE" })}
          onRemovePhone={(idx) => dispatch({ type: "REMOVE_PHONE", index: idx })}
        />

        <AddressList
          addresses={state.addresses}
          selectedAddressIdx={state.selectedAddressIdx}
          onSetAddress={(idx, field, val) =>
            dispatch({ type: "SET_ADDRESS", index: idx, field: field as keyof AddressEntry, value: val })
          }
          onAddAddress={() => dispatch({ type: "ADD_ADDRESS" })}
          onRemoveAddress={(idx) => dispatch({ type: "REMOVE_ADDRESS", index: idx })}
          onSelectAddress={(idx) => dispatch({ type: "SELECT_ADDRESS", index: idx })}
        />

        <TagSelector
          label={t("orderCreate.clientTags")}
          selectedTagIds={state.clientTags}
          availableTags={clientTags}
          onChange={(tagId) => dispatch({ type: "TOGGLE_CLIENT_TAG", tagId })}
        />

        <TextField
          label={t("orderCreate.customerNotes")}
          value={state.customerNotes}
          onChange={(e) => dispatch({ type: "SET_CUSTOMER_NOTES", value: e.target.value })}
          size="small"
          fullWidth
          multiline
          rows={2}
          data-testid="order-create-customer-notes"
        />
      </Stack>
    </Box>
  )
}
